import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import ccmService from '../../services/ccmService';
import './EnrollPatient.css'; // Relies on upgraded premium css
import AppDateInput from '../../components/Common/AppDateInput/AppDateInput';

const EnrollPatient = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [step, setStep] = useState(1);
  
  const [enrollmentData, setEnrollmentData] = useState({
    patient_id: patientId || '',
    practice_id: '1', // Defaulting for simple flow demo
    program_type: 'CCM',
    enrollment_date: new Date().toISOString().split('T')[0],
    billing_provider_id: '1',
    care_manager_id: '2',
    care_coordinator_id: '3',
    primary_provider_id: '1',
    icd_codes: [],
    consent_obtained: false,
    consent_date: new Date().toISOString().split('T')[0],
    enrollment_notes: '',
    consent_file: null
  });

  useEffect(() => {
    if (patientId) {
      checkEligibility();
    } else {
      setLoading(false);
    }
  }, [patientId]);

  const checkEligibility = async () => {
    try {
      setLoading(true);
      const response = await ccmService.checkEligibility(patientId, 'CCM');
      if (response.success) {
        setEligibility(response.data);
        setEnrollmentData(prev => ({
          ...prev,
          patient_id: patientId,
          icd_codes: response.data.chronic_conditions || []
        }));
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
      // Mock data for demo purposes if backend fails
      setEligibility({
        is_eligible: true,
        chronic_conditions_count: 3,
        is_medicare_eligible: true,
        eligibility_reasons: ['Medicare eligible', 'Has 3 chronic conditions'],
        chronic_conditions: [{icd_code: 'I10', description: 'Hypertension'}, {icd_code: 'E11.9', description: 'Type 2 Diabetes'}]
      });
      setEnrollmentData(prev => ({
        ...prev, patient_id: patientId, icd_codes: [{icd_code: 'I10'}, {icd_code: 'E11.9'}]
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!enrollmentData.consent_obtained) {
      alert('Consent must be obtained before enrollment');
      return;
    }

    try {
      setEnrolling(true);
      // Wait for 1 second to simulate upload/processing
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const response = await ccmService.enrollPatient(enrollmentData);
      if (response.success) {
        // Automatically route to PHM Hub for workflow continuitity
        navigate(`/ccm/phm-hub/${response.data.enrollment_id}`);
      }
    } catch (error) {
      console.error('Error enrolling patient:', error);
      // Fallback for demo workflow jump
      navigate(`/ccm/phm-hub/demoid`);
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="ccm-premium-container d-flex justify-content-center align-items-center" style={{height: '80vh'}}>
        <div className="text-center text-white">
          <Spinner animation="grow" variant="light" />
          <h4 className="mt-3">Analyzing Patient Eligibility...</h4>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="ccm-premium-container py-5">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          
          <div className="premium-header mb-5 text-center">
            <h2 className="title-gradient">Chronic Care Management Enrollment</h2>
            <p className="subtitle">Streamlined workflow for Medicare CCM compliance</p>
          </div>

          {/* Stepper Logic Component */}
          <div className="stepper-wrapper mb-5">
            <div className={`stepper-item ${step >= 1 ? 'completed' : ''}`}>
              <div className="step-counter">1</div>
              <div className="step-name">Verification</div>
            </div>
            <div className={`stepper-item ${step >= 2 ? 'completed' : ''}`}>
               <div className="step-counter">2</div>
               <div className="step-name">Care Team</div>
            </div>
            <div className={`stepper-item ${step >= 3 ? 'completed' : ''}`}>
              <div className="step-counter">3</div>
              <div className="step-name">Consent</div>
            </div>
          </div>

          <Card className="premium-glass-card shadow-lg">
            <Card.Body className="p-5">
              
              {/* STEP 1: Verification */}
              {step === 1 && (
                <div className="step-content fade-in">
                  <h4 className="mb-4 d-flex align-items-center">
                    <i className="bi bi-shield-check text-success fs-3 me-2"></i> Eligibility Verification
                  </h4>
                  
                  {eligibility ? (
                    <Alert variant={eligibility.is_eligible ? 'success' : 'danger'} className="glass-alert">
                       {eligibility.is_eligible ? (
                         <div>
                          <h5><i className="bi bi-check-circle-fill"></i> Patient is Eligible for CCM</h5>
                          <ul className="mt-3">
                            {eligibility.eligibility_reasons.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                         </div>
                       ) : (
                         <div>
                          <h5><i className="bi bi-x-circle-fill"></i> Patient is Not Eligible</h5>
                          <ul className="mt-3">
                            {eligibility.missing_requirements.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                         </div>
                       )}
                    </Alert>
                  ) : (
                    <Alert variant="warning">Awaiting Patient Selection</Alert>
                  )}

                  <div className="mt-4">
                    <h5 className="text-white-50 mb-3">Qualifying Chronic Conditions</h5>
                    <div className="d-flex flex-wrap gap-2">
                       {enrollmentData.icd_codes.map((code, idx) => (
                         <Badge bg="primary" className="p-2 fs-6 rounded-pill" key={idx}>
                           {code.icd_code} {code.description && `- ${code.description}`}
                         </Badge>
                       ))}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end mt-5">
                    <Button 
                      variant="primary" 
                      className="premium-btn px-4 py-2" 
                      disabled={!eligibility?.is_eligible}
                      onClick={() => setStep(2)}>
                        Next: Assign Care Team <i className="bi bi-arrow-right ms-2"></i>
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 2: Care Team */}
              {step === 2 && (
                <div className="step-content fade-in">
                  <h4 className="mb-4 text-white">Assign Dedicated Care Team</h4>
                  
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="text-white-50">Billing Provider (MD/DO)</Form.Label>
                        <Form.Select 
                          className="premium-input"
                          value={enrollmentData.billing_provider_id}
                          onChange={(e) => setEnrollmentData({...enrollmentData, billing_provider_id: e.target.value})}>
                          <option value="1">Dr. Sarah Jenkins (Cardiology)</option>
                          <option value="4">Dr. Marcus Webb (Internal Medicine)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="text-white-50">Primary Provider</Form.Label>
                        <Form.Select 
                          className="premium-input"
                          value={enrollmentData.primary_provider_id}
                          onChange={(e) => setEnrollmentData({...enrollmentData, primary_provider_id: e.target.value})}>
                           <option value="1">Dr. Sarah Jenkins</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="text-white-50">Clinical Care Manager (RN)</Form.Label>
                        <Form.Select 
                          className="premium-input"
                          value={enrollmentData.care_manager_id}
                          onChange={(e) => setEnrollmentData({...enrollmentData, care_manager_id: e.target.value})}>
                           <option value="2">Emily Chen, RN</option>
                           <option value="5">Robert Davis, RN</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="text-white-50">Care Coordinator</Form.Label>
                        <Form.Select 
                          className="premium-input"
                          value={enrollmentData.care_coordinator_id}
                          onChange={(e) => setEnrollmentData({...enrollmentData, care_coordinator_id: e.target.value})}>
                           <option value="3">Jessica Thompson</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-between mt-5">
                    <Button variant="outline-light" onClick={() => setStep(1)}>
                       <i className="bi bi-arrow-left me-2"></i> Back
                    </Button>
                    <Button variant="primary" className="premium-btn px-4 py-2" onClick={() => setStep(3)}>
                        Next: Patient Consent <i className="bi bi-arrow-right ms-2"></i>
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 3: Consent */}
              {step === 3 && (
                <div className="step-content fade-in">
                  <h4 className="mb-4 text-white">Patient Consent & Finalization</h4>
                  
                  <div className="consent-box p-4 rounded mb-4">
                    <Form.Group className="mb-4">
                      <Form.Check
                        type="switch"
                        id="consent-switch"
                        label="Verbal/Written Consent Obtained for CCM Services"
                        className="consent-toggle fs-5 text-white"
                        checked={enrollmentData.consent_obtained}
                        onChange={(e) => setEnrollmentData({ ...enrollmentData, consent_obtained: e.target.checked })}
                      />
                      <Form.Text className="text-white-50 mt-2 d-block">
                        I verify that the patient agrees to comprehensive care management and has been notified of potential copays.
                      </Form.Text>
                    </Form.Group>

                    {enrollmentData.consent_obtained && (
                      <div className="fade-in">
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="text-white-50">Consent Date</Form.Label>
                              <AppDateInput
                                className="premium-input form-control"
                                value={enrollmentData.consent_date}
                                onChange={(v) => setEnrollmentData({ ...enrollmentData, consent_date: v })}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="text-white-50">Upload Signed Consent (Optional)</Form.Label>
                              <Form.Control
                                type="file"
                                className="premium-input file-input"
                                onChange={(e) => setEnrollmentData({ ...enrollmentData, consent_file: e.target.files[0] })}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white-50">Enrollment Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            className="premium-input"
                            placeholder="Additional context from consent discussion..."
                            value={enrollmentData.enrollment_notes}
                            onChange={(e) => setEnrollmentData({ ...enrollmentData, enrollment_notes: e.target.value })}
                          />
                        </Form.Group>
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between mt-5">
                    <Button variant="outline-light" onClick={() => setStep(2)}>
                       <i className="bi bi-arrow-left me-2"></i> Back
                    </Button>
                    <Button 
                      variant="success" 
                      className="premium-btn btn-success px-5 py-2 fw-bold text-white shadow-lg" 
                      disabled={enrolling || !enrollmentData.consent_obtained}
                      onClick={handleEnroll}>
                        {enrolling ? (
                          <><Spinner size="sm" className="me-2"/> Enrolling...</>
                        ) : (
                          <><i className="bi bi-check-circle-fill me-2"></i> Finalize & Open PHM Hub</>
                        )}
                    </Button>
                  </div>
                </div>
              )}

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EnrollPatient;
